// ─── 디바이스 타입 정의 ───

export enum DeviceType {
  LITE = 'belllite',
  MINI = 'bellmini',
  // 새 기기 추가시 여기에 enum 값 추가 후 DEVICE_TYPE_CONFIGS에 설정 등록
}

/**
 * fn_params·패킷 유틸에서 쓰는 프로토콜 식별자. DeviceType enum 값과 동일 (`belllite` / `bellmini`).
 */
export type ProtocolType = DeviceType;

// ─── 디바이스 타입별 설정 레지스트리 ───

export interface DeviceTypeConfig {
  /**
   * 수신 패킷 헤더 Source 바이트 (packet[2]). 프로토콜 문서 endpoint ID.
   * 예: RATEL(BELLMINI)=0x01, Bulls(BELLLITE)=0x05
   */
  incomingPacketSourceByte: number;
  /** 시리얼 번호에 포함되는 접두사 (예: 'BMI', 'BLI') */
  serialPrefixes: string[];
  /** BLE advertise 이름 접두사 (예: 'BELLMINI_') */
  blePrefixes: string[];
  /** DB 테이블명 */
  tables: {
    monitoring: string;
    information: string;
    configuration: string;
  };
  /** 센서 모드: single(Mini 계열) / multi(Lite 계열) */
  sensorMode: 'single' | 'multi';
  /** 알람 비트마스크 (system_status / sensor_status 해석용) */
  alarmBits: {
    lowAlarm: number;
    highAlarm: number;
    stelAlarm: number;
    twaAlarm: number;
  };
}

const DEVICE_TYPE_CONFIGS: Record<DeviceType, DeviceTypeConfig> = {
  [DeviceType.LITE]: {
    incomingPacketSourceByte: 0x05, // Bulls (BELL LITE)
    serialPrefixes: ['BLI'],
    blePrefixes: ['BELLLITE_'],
    tables: {
      monitoring: 'lite_monitoring',
      information: 'lite_information',
      configuration: 'lite_configuration',
    },
    sensorMode: 'multi',
    alarmBits: {
      lowAlarm: 0x0010,
      highAlarm: 0x0020,
      stelAlarm: 0x0200,
      twaAlarm: 0x0400,
    },
  },
  [DeviceType.MINI]: {
    incomingPacketSourceByte: 0x01, // RATEL (BELLMINI)
    serialPrefixes: ['BMI'],
    blePrefixes: ['BELLMINI_'],
    tables: {
      monitoring: 'mini_monitoring',
      information: 'mini_information',
      configuration: 'mini_configuration',
    },
    sensorMode: 'single',
    alarmBits: {
      lowAlarm: 0x0004,
      highAlarm: 0x0008,
      stelAlarm: 0x0800,
      twaAlarm: 0x1000,
    },
  },
  // 새 기기 타입 추가 예시:
  // [DeviceType.ALPHA]: {
  //   incomingPacketSourceByte: 0x??, // 문서의 endpoint Source ID
  //   serialPrefixes: ['BAL'],
  //   blePrefixes: ['BELLALPHA_'],
  //   tables: { monitoring: 'alpha_monitoring', ... },
  //   sensorMode: 'single',
  //   alarmBits: { ... },
  // },
};

/** 모든 등록된 DeviceType 값 목록 */
export const ALL_DEVICE_TYPES = Object.values(DeviceType);

/** DeviceType에 해당하는 config 조회 (타입 안전) */
export function getDeviceTypeConfig(deviceType: DeviceType): DeviceTypeConfig {
  return DEVICE_TYPE_CONFIGS[deviceType];
}

/**
 * 수신 패킷 Source 바이트로 디바이스 타입 판별 (`DEVICE_TYPE_CONFIGS.incomingPacketSourceByte` 기준).
 * 매칭 없으면 null → 시리얼 등으로 폴백.
 */
export function detectDeviceTypeFromPacketSource(sourceByte: number): DeviceType | null {
  for (const [type, config] of Object.entries(DEVICE_TYPE_CONFIGS)) {
    if (config.incomingPacketSourceByte === sourceByte) {
      return type as DeviceType;
    }
  }
  return null;
}

// ─── 디바이스 타입 감지 (중앙 집중) ───

/**
 * 시리얼 번호로 디바이스 타입 감지.
 * DEVICE_TYPE_CONFIGS의 serialPrefixes를 순회하므로 새 타입 추가시 자동 반영.
 */
export function detectDeviceTypeFromSerial(serialNumber: string): DeviceType {
  const upper = serialNumber.toUpperCase().trim();
  for (const [type, config] of Object.entries(DEVICE_TYPE_CONFIGS)) {
    if (config.serialPrefixes.some(prefix => upper.includes(prefix))) {
      return type as DeviceType;
    }
  }
  return DeviceType.LITE;
}

/**
 * BLE advertise 이름으로 디바이스 타입 감지.
 */
export function detectDeviceTypeFromName(deviceName: string): DeviceType | null {
  for (const [type, config] of Object.entries(DEVICE_TYPE_CONFIGS)) {
    if (config.blePrefixes.some(prefix => deviceName.startsWith(prefix))) {
      return type as DeviceType;
    }
  }
  return null;
}

/**
 * device_type 문자열 또는 시리얼 번호로 DeviceType 결정.
 * DB에서 읽은 device_type 문자열을 정규화하는 데 사용.
 */
export function resolveDeviceType(
  deviceTypeStr?: string | null,
  serialNumber?: string
): DeviceType {
  if (deviceTypeStr) {
    const lower = deviceTypeStr.toLowerCase();
    for (const enumVal of ALL_DEVICE_TYPES) {
      if (lower === enumVal || lower.includes(enumVal.replace('bell', ''))) {
        return enumVal;
      }
    }
  }
  if (serialNumber) {
    return detectDeviceTypeFromSerial(serialNumber);
  }
  return DeviceType.LITE;
}

/** DeviceType과 프로토콜 레이어 식별자는 1:1 동일 값 사용 */
export function getProtocolForDeviceType(deviceType: DeviceType): ProtocolType {
  return deviceType;
}

// ─── 기존 호환 인터페이스 ───

export interface ConnectedDeviceInfo {
  type: DeviceType;
  name: string;
  serialNumber?: string;
  sensors?: Array<{
    type: string;
    label: string;
  }>;
  sensor?: {
    type: string;
    label: string;
  };
}

interface DeviceTypeIdentifier {
  device_type?: string | number;
  serial_number: string;
}


/** @deprecated resolveDeviceType 사용 권장 */
export function getDeviceType(data: DeviceTypeIdentifier): DeviceType {
  return resolveDeviceType(
    typeof data.device_type === 'string' ? data.device_type : undefined,
    data.serial_number
  );
}
